/**
 * AG Grid Module Registration Utility
 * 
 * This utility allows lazy loading of AG Grid modules only when needed,
 * instead of loading them all upfront in App.js
 * 
 * Usage:
 * import { initializeAgGridModules } from '../Utils/agGridModules';
 * 
 * useEffect(() => {
 *   initializeAgGridModules();
 * }, []);
 */

import { ModuleRegistry } from 'ag-grid-community';
import {
  InfiniteRowModelModule,
  AllCommunityModule,
  PaginationModule,
  ClientSideRowModelModule
} from 'ag-grid-community';
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  GroupFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
  ColumnMenuModule,
  ContextMenuModule,
  RowDragModule,
  ExcelExportModule,
} from 'ag-grid-enterprise';

let modulesInitialized = false;

/**
 * Initialize AG Grid modules for basic/common usage
 * This is the minimal set needed for most grids
 */
export const initializeAgGridModules = () => {
  if (modulesInitialized) {
    return; // Already initialized
  }

  // Register modules (AG Grid v33 doesn't have isRegistered method)
  ModuleRegistry.registerModules([
    InfiniteRowModelModule,
    AllCommunityModule,
    PaginationModule, // Required for server-side pagination
    ClientSideRowModelModule
  ]);

  modulesInitialized = true;
};

/**
 * Initialize AG Grid Enterprise modules
 * Call this when you need enterprise features
 */
export const initializeAgGridEnterpriseModules = () => {
  if (window.__agGridEnterpriseInitialized) {
    return; // Already initialized
  }

  // First ensure basic modules are initialized
  initializeAgGridModules();

  // Register enterprise modules
  ModuleRegistry.registerModules([
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    MenuModule,
    SetFilterModule,
    ServerSideRowModelModule,
    ServerSideRowModelApiModule,
  ]);

  window.__agGridEnterpriseInitialized = true;
};

/**
 * Initialize AG Grid modules for advanced features (grouping, pivoting, etc.)
 * Call this when you need advanced enterprise features
 */
export const initializeAgGridAdvancedModules = () => {
  if (window.__agGridAdvancedInitialized) {
    return; // Already initialized
  }

  // First ensure enterprise modules are initialized
  initializeAgGridEnterpriseModules();

  // Register advanced modules
  ModuleRegistry.registerModules([
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
    ColumnMenuModule,
    ContextMenuModule,
    RowDragModule,
    ExcelExportModule,
  ]);

  window.__agGridAdvancedInitialized = true;
};

/**
 * Initialize all AG Grid modules (for backward compatibility)
 * Use specific initialization functions above for better code splitting
 */
export const initializeAllAgGridModules = () => {
  initializeAgGridAdvancedModules();
};
